import { useEffect, useMemo, useState } from 'preact/hooks';
import { AppLayout } from '../../components/app/AppLayout';
import { RequireOnboarded } from '../../components/app/RequireOnboarded';
import { useI18n } from '../../i18n';
import { useApiAuth } from '../../lib/useApi';
import { useGameData } from '../../state/game';
import { BottleCap } from '../../components/app/BottleCap';
import { CantonShield } from '../../components/app/CantonShield';
import { Icon } from '../../components/app/icons';
import { PredictionRow, PredictionGroup, type PredRow } from '../../components/app/PredictionList';
import { sideLabel, type Bundle } from '../../lib/wcTypes';
import { shortDate, shortTime } from '../../lib/datetime';
import {
  getPublicProfile,
  getPublicPredictions,
  type PublicProfile as PublicProfileT,
  type PublicPrediction,
} from '../../lib/publicProfile';

export function PublicProfile({ id }: { id: string }) {
  return (
    <RequireOnboarded>
      <AppLayout>
        <PublicProfileInner id={id} />
      </AppLayout>
    </RequireOnboarded>
  );
}

function PublicProfileInner({ id }: { id: string }) {
  const { t, locale, localePath } = useI18n();
  const auth = useApiAuth();
  const { bundle } = useGameData();

  const [profile, setProfile] = useState<PublicProfileT | null>(null);
  const [preds, setPreds] = useState<PublicPrediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    Promise.all([getPublicProfile(auth, id), getPublicPredictions(auth, id)])
      .then(([p, pp]) => {
        if (!alive) return;
        setProfile(p);
        setPreds(pp.predictions);
      })
      .catch(() => alive && setNotFound(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  // Stats derived from the visibility-filtered payload. Points only exist on
  // finished matches, which every visibility policy exposes, so the totals
  // match what the leaderboard would show.
  const { picks, points, accuracy, played } = useMemo(() => {
    let points = 0;
    let played = 0;
    let correct = 0;
    for (const p of preds) {
      if (typeof p.points === 'number') points += p.points;
      if (p.matchStatus === 'done' && !p.hidden && p.actualA != null && p.actualB != null && p.scoreA != null && p.scoreB != null) {
        played++;
        if (outcome(p.scoreA, p.scoreB) === outcome(p.actualA, p.actualB)) correct++;
      }
    }
    return { picks: preds.length, points, accuracy: played ? Math.round((correct / played) * 100) : 0, played };
  }, [preds]);

  if (loading) return <div class="bc-spinner">{t('app.loading')}</div>;
  if (notFound || !profile) {
    return (
      <div class="bc-empty">
        {t('pubprofile.notfound')}
        <div style={{ marginTop: 16 }}>
          <a class="bc-pill" href={localePath('/boards')}>{t('boards.back')}</a>
        </div>
      </div>
    );
  }

  const rows = preds.map((p) => toPredRow(p, bundle, locale));
  const active = rows
    .filter((r) => r.status !== 'done')
    .sort((a, b) => a.matchNumber - b.matchNumber);
  const previous = rows
    .filter((r) => r.status === 'done')
    .sort((a, b) => b.matchNumber - a.matchNumber);

  return (
    <div class="pf pub">
      <a class="md__back" href={localePath('/boards')}>
        <Icon.back size={18} /> {t('boards.back')}
      </a>

      <div class="pf__head">
        <div class="pf__avatar">
          {profile.avatarUrl ? (
            <img src={profile.avatarUrl} alt="" />
          ) : (
            <BottleCap code={profile.avatarCap} size={72} />
          )}
        </div>
        <div class="pf__id">
          <h1 class="bc-serif">{profile.nickname}</h1>
          {profile.canton && (
            <p>
              <CantonShield code={profile.canton} size={16} /> {profile.canton}
            </p>
          )}
        </div>
      </div>

      <div class="pf__stats">
        <Stat label={t('profile.points')} value={points} />
        <Stat label={t('profile.picks')} value={picks} />
        <Stat label={t('profile.accuracy')} value={played ? `${accuracy}%` : '–'} />
      </div>

      <section class="pf__preds">
        <span class="bc-label">{t('pubprofile.predictions')}</span>
        {rows.length === 0 ? (
          <p class="bc-empty">{t('pubprofile.no_predictions')}</p>
        ) : (
          <>
            {active.length > 0 && (
              <PredictionGroup label={t('preds.active')} count={active.length}>
                {active.map((r) => (
                  <PredictionRow key={r.matchNumber} row={r} />
                ))}
              </PredictionGroup>
            )}
            {previous.length > 0 && (
              <PredictionGroup label={t('preds.previous')} count={previous.length} defaultOpen={false} accent="mute">
                {previous.map((r) => (
                  <PredictionRow key={r.matchNumber} row={r} />
                ))}
              </PredictionGroup>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/** Map a server PublicPrediction onto the shared row shape, using the bundle
 *  for team caps/flags/labels (falls back to "#N" when the bundle is cold). */
function toPredRow(p: PublicPrediction, bundle: Bundle | null, locale: ReturnType<typeof useI18n>['locale']): PredRow {
  const m = bundle?.matches.find((x) => x.number === p.matchNumber);
  const labelA = m && bundle ? sideLabel(bundle, m.team_a, m.team_a_slot, locale) : `#${p.matchNumber}`;
  const labelB = m && bundle ? sideLabel(bundle, m.team_b, m.team_b_slot, locale) : '';
  const dateLabel = `${shortDate(p.kickoffUtc, locale)} · ${shortTime(p.kickoffUtc, locale)}`;
  return {
    matchNumber: p.matchNumber,
    capA: m?.team_a,
    flagA: m?.team_a ? bundle?.teams[m.team_a]?.flag : undefined,
    labelA,
    capB: m?.team_b,
    flagB: m?.team_b ? bundle?.teams[m.team_b]?.flag : undefined,
    labelB,
    status: p.matchStatus,
    hidden: p.hidden,
    predictedA: p.scoreA,
    predictedB: p.scoreB,
    actualA: p.actualA,
    actualB: p.actualB,
    points: p.points,
    boost: p.boost,
    dateLabel,
  };
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div class="pf__stat">
      <span class="pf__statval">{value}</span>
      <span class="pf__statlbl">{label}</span>
    </div>
  );
}

function outcome(a: number, b: number): '1' | 'X' | '2' {
  return a > b ? '1' : a < b ? '2' : 'X';
}
